// file name: CommentSection.tsx
import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  createComment, 
  getCommentsByTarget, 
  rateComment,
  deleteComment,
  updateComment,
  CommentData,
  CommentTargetType 
} from '../../firebase/commentService';
import { 
  FaUser, 
  FaClock, 
  FaHeart, 
  FaThumbsDown, 
  FaEdit, 
  FaTrash, 
  FaReply,
  FaPaperPlane,
  FaCheck,
  FaTimes
} from '../../utils/iconUtils';
import { motion, AnimatePresence } from 'framer-motion';

interface CommentSectionProps {
  targetId?: string;
  targetType?: CommentTargetType;
  newsId?: string; // Backward compatibility for existing call sites
}

const CommentSection: React.FC<CommentSectionProps> = ({
  targetId,
  targetType = 'news',
  newsId
}) => {
  const { currentUser, userProfile } = useAuth();
  const resolvedTargetId = targetId || newsId || '';
  const resolvedTargetType: CommentTargetType = targetType;
  const [comments, setComments] = useState<CommentData[]>([]);
  const [commentCount, setCommentCount] = useState(0);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [replyOpenIds, setReplyOpenIds] = useState<Record<string, boolean>>({});
  const [replyContentById, setReplyContentById] = useState<Record<string, string>>({});
  const [replySubmittingId, setReplySubmittingId] = useState<string | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  // Fetch comments
  useEffect(() => {
    if (!resolvedTargetId) {
      setComments([]);
      setCommentCount(0);
      return;
    }
    fetchComments();
  }, [resolvedTargetId, resolvedTargetType]);

  const fetchComments = async () => {
    if (!resolvedTargetId) return;
    setLoading(true);
    const result = await getCommentsByTarget(resolvedTargetId, resolvedTargetType);
    if (result.success) {
      setComments(result.data);
      setCommentCount(result.data.length);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!resolvedTargetId) return;
    
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    if (!newComment.trim()) return;

    setSubmitting(true);
    const commentData = {
      targetId: resolvedTargetId,
      targetType: resolvedTargetType,
      newsId: resolvedTargetType === 'news' ? resolvedTargetId : undefined,
      userId: currentUser.uid,
      userEmail: currentUser.email || '',
      userName: userProfile?.displayName || userProfile?.firstName + ' ' + userProfile?.lastName || 'Anonymous',
      userPhotoURL: (userProfile as any)?.profilePhoto || (userProfile as any)?.profileImage || currentUser.photoURL || '',
      content: newComment.trim(),
    };

    const result = await createComment(commentData);
    
    if (result.success) {
      setNewComment('');
      fetchComments();
    }
    
    setSubmitting(false);
  };

  const handleEdit = async (commentId: string) => {
    if (!editContent.trim()) return;
    
    const result = await updateComment(commentId, { content: editContent });
    if (result.success) {
      setEditingCommentId(null);
      fetchComments();
    }
  };

  const handleReplySubmit = async (parentCommentId: string) => {
    if (!resolvedTargetId) return;

    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }

    const content = (replyContentById[parentCommentId] || '').trim();
    if (!content) return;

    setReplySubmittingId(parentCommentId);

    const commentData = {
      targetId: resolvedTargetId,
      targetType: resolvedTargetType,
      newsId: resolvedTargetType === 'news' ? resolvedTargetId : undefined,
      userId: currentUser.uid,
      userEmail: currentUser.email || '',
      userName: userProfile?.displayName || userProfile?.firstName + ' ' + userProfile?.lastName || 'Anonymous',
      userPhotoURL: (userProfile as any)?.profilePhoto || (userProfile as any)?.profileImage || currentUser.photoURL || '',
      content,
      parentCommentId
    };

    const result = await createComment(commentData);
    if (result.success) {
      setReplyContentById(prev => ({ ...prev, [parentCommentId]: '' }));
      setReplyOpenIds(prev => ({ ...prev, [parentCommentId]: false }));
      fetchComments();
    }

    setReplySubmittingId(null);
  };

  const handleDelete = async (commentId: string) => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      const result = await deleteComment(commentId);
      if (result.success) {
        fetchComments();
      }
    }
  };

  const handleRate = async (commentId: string, action: 'like' | 'dislike') => {
    if (!currentUser) {
      setShowLoginPrompt(true);
      return;
    }
    
    const result = await rateComment(commentId, action, currentUser.uid);
    if (result.success) {
      fetchComments();
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Organize comments into top-level and replies
  const topLevelComments = comments.filter(c => !c.parentCommentId);
  const repliesMap: Record<string, CommentData[]> = {};
  comments.forEach(c => {
    if (c.parentCommentId) {
      repliesMap[c.parentCommentId] = repliesMap[c.parentCommentId] || [];
      repliesMap[c.parentCommentId].push(c);
    }
  });

  return (
    <div className="mt-12 border-t border-gray-200 pt-8">
      {/* Comment Count */}
              <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">
          Comments ({commentCount})
        </h3>
        {!currentUser && (
          <p className="text-sm text-gray-600">
            Please log in to leave a comment
          </p>
        )}
      </div>

      {/* Login Prompt Modal */}
      <AnimatePresence>
        {showLoginPrompt && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Sign In Required
              </h3>
              <p className="text-gray-600 mb-6">
                You need to be signed in to post comments.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLoginPrompt(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => window.location.href = '/login'}
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Sign In
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Comment Form */}
      <div className="mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              {currentUser?.photoURL ? (
                <img
                  src={currentUser.photoURL}
                  alt={currentUser.displayName || 'User'}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <FaUser className="text-primary-600" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={currentUser ? "Share your thoughts..." : "Sign in to leave a comment..."}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                rows={3}
                disabled={!currentUser || submitting}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-sm text-gray-500">
                  {currentUser ? `Commenting as ${userProfile?.displayName || currentUser.email}` : 'Please sign in'}
                </p>
                <button
                  type="submit"
                  disabled={!resolvedTargetId || !newComment.trim() || submitting || !currentUser}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Posting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="mr-2" />
                      Post Comment
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Comments List */}
      <div className="space-y-6">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8 border border-gray-200 rounded-lg">
            <p className="text-gray-600">No comments yet. Be the first to share your thoughts!</p>
          </div>
        ) : (
          topLevelComments.map((comment) => (
            <div key={comment.id}>
              {renderComment(comment, 0)}
            </div>
          ))
        )}
      </div>
    </div>
  );

  function renderComment(comment: CommentData, depth: number) {
    const children = repliesMap[comment.id!] || [];
    return (
      <div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-lg p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              {comment.userPhotoURL ? (
                <img src={comment.userPhotoURL} alt={comment.userName} className="w-8 h-8 rounded-full" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <FaUser className="text-gray-500 text-sm" />
                </div>
              )}
              <div>
                <h4 className="font-semibold text-gray-900">{comment.userName}</h4>
                <div className="flex items-center text-sm text-gray-500">
                  <FaClock className="mr-1" />
                  <span>{formatDate(comment.createdAt)}</span>
                  {comment.isEdited && <span className="ml-2 text-xs text-gray-400">(edited)</span>}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              {currentUser?.uid === comment.userId && (
                <>
                  <button
                    onClick={() => { setEditingCommentId(comment.id!); setEditContent(comment.content); }}
                    className="p-1 text-gray-500 hover:text-primary-600 transition-colors"
                  >
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(comment.id!)} className="p-1 text-gray-500 hover:text-red-600 transition-colors">
                    <FaTrash />
                  </button>
                </>
              )}

              <button
                onClick={() => { if (!currentUser) { setShowLoginPrompt(true); return; } setReplyOpenIds(prev => ({ ...prev, [comment.id!]: !prev[comment.id!] })); }}
                className="p-1 text-gray-500 hover:text-primary-600 transition-colors flex items-center"
              >
                <FaReply className="mr-1" />
                <span className="text-sm">Reply</span>
              </button>
            </div>
          </div>

          {editingCommentId === comment.id ? (
            <div className="space-y-3 mb-3">
              <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" rows={3} />
              <div className="flex space-x-2">
                <button onClick={() => handleEdit(comment.id!)} className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center">
                  <FaCheck className="mr-1" /> Save
                </button>
                <button onClick={() => setEditingCommentId(null)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center">
                  <FaTimes className="mr-1" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="text-gray-800 mb-3">{comment.content}</p>
          )}

          <div className="flex items-center justify-between border-t border-gray-100 pt-3">
            <div className="flex items-center space-x-4">
              <button onClick={() => handleRate(comment.id!, 'like')} className={`flex items-center space-x-1 ${currentUser ? 'hover:text-green-600' : 'cursor-default'}`} disabled={!currentUser}>
                <FaHeart className={`${currentUser ? 'hover:fill-current' : ''}`} />
                <span>{comment.likes || 0}</span>
              </button>
              <button onClick={() => handleRate(comment.id!, 'dislike')} className={`flex items-center space-x-1 ${currentUser ? 'hover:text-red-600' : 'cursor-default'}`} disabled={!currentUser}>
                <FaThumbsDown className={`${currentUser ? 'hover:fill-current' : ''}`} />
                <span>{comment.dislikes || 0}</span>
              </button>
            </div>
          </div>
        </motion.div>

        {replyOpenIds[comment.id!] && (
          <div className="ml-8 mt-3">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                {currentUser?.photoURL ? (
                  <img src={currentUser.photoURL} alt={currentUser.displayName || 'User'} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <FaUser className="text-primary-600" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <textarea
                  value={replyContentById[comment.id!] || ''}
                  onChange={(e) => setReplyContentById(prev => ({ ...prev, [comment.id!]: e.target.value }))}
                  placeholder={currentUser ? 'Write a reply...' : 'Sign in to reply...'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  rows={2}
                  disabled={!currentUser || replySubmittingId === comment.id}
                />
                <div className="flex items-center justify-end space-x-2 mt-2">
                  <button onClick={() => setReplyOpenIds(prev => ({ ...prev, [comment.id!]: false }))} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                  <button onClick={() => handleReplySubmit(comment.id!)} disabled={!replyContentById[comment.id!]?.trim() || replySubmittingId === comment.id} className="px-3 py-1 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                    {replySubmittingId === comment.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> : <FaPaperPlane className="mr-2" />} Reply
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {children.map(child => (
          <div key={child.id} className="mt-4 ml-8">{renderComment(child, depth + 1)}</div>
        ))}
      </div>
    );
  }

}

export default CommentSection;
